/**
 * StatusFooter Component
 *
 * Displays comprehensive system status in a footer:
 * - System health (API, DB, Redis)
 * - Worker status (streaming, main, beat)
 * - Per-account streaming status
 *
 * Polls the /system/status endpoint every 10 seconds
 */

import React, { useEffect, useState } from 'react'
import { apiClient } from '../api/client'

interface SystemHealth {
  api: string
  database: string
  redis: string
}

interface WorkerStatus {
  streaming_worker: {
    status: string
    active_tasks: number
  }
  main_worker: {
    status: string
    active_tasks: number
  }
  beat: {
    status: string
  }
}

interface AccountStreamingStatus {
  account_id: number
  account_name: string
  account_type: string
  streaming: {
    active: boolean
    subscribed_instruments: number
    last_tick_time: string | null
    health: string
  }
}

interface SystemStatus {
  timestamp: string
  system: SystemHealth
  workers: WorkerStatus
  accounts: AccountStreamingStatus[]
}

const StatusIndicator: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const getColor = () => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'active':
        return 'bg-neon-green'
      case 'unhealthy':
      case 'inactive':
        return 'bg-neon-pink'
      default:
        return 'bg-arcade-dim'
    }
  }

  return (
    <div className="flex items-center space-x-1.5">
      <div className={`w-2 h-2 rounded-full ${getColor()}`} />
      <span className="text-xs text-arcade-muted">{label}</span>
    </div>
  )
}

export const StatusFooter: React.FC = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const fetchStatus = async () => {
    try {
      const response = await apiClient.get<SystemStatus>('/system/status')
      setStatus(response.data)
      setError(null)
    } catch (err: any) {
      console.error('Failed to fetch system status:', err)
      setError('Unable to fetch system status')
    }
  }

  useEffect(() => {
    // Fetch immediately on mount
    fetchStatus()

    // Poll every 10 seconds
    const interval = setInterval(fetchStatus, 10000)

    return () => clearInterval(interval)
  }, [])

  if (error) {
    return (
      <footer className="bg-dark-800 border-t border-dark-600 px-4 py-2">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neon-pink">{error}</span>
          </div>
        </div>
      </footer>
    )
  }

  if (!status) {
    return (
      <footer className="bg-dark-800 border-t border-dark-600 px-4 py-2">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <span className="text-xs text-arcade-dim">Loading system status...</span>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className="bg-dark-800 border-t border-dark-600 px-4 py-2">
      <div className="max-w-7xl mx-auto">
        {/* Compact view */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {/* System Health Summary */}
            <div className="flex items-center space-x-3">
              <StatusIndicator status={status.system.api} label="API" />
              <StatusIndicator status={status.system.database} label="DB" />
              <StatusIndicator status={status.system.redis} label="Redis" />
            </div>

            {/* Workers Summary */}
            <div className="flex items-center space-x-3 border-l border-dark-600 pl-6">
              <StatusIndicator status={status.workers.streaming_worker.status} label="Stream" />
              <StatusIndicator status={status.workers.main_worker.status} label="Worker" />
              <StatusIndicator status={status.workers.beat.status} label="Beat" />
            </div>

            {/* Accounts Summary */}
            {status.accounts.length > 0 && (
              <div className="flex items-center space-x-3 border-l border-dark-600 pl-6">
                {status.accounts.map((account) => (
                  <div key={account.account_id} className="flex items-center space-x-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        account.streaming.active ? 'bg-neon-green' : 'bg-arcade-dim'
                      }`}
                    />
                    <span className="text-xs text-arcade-muted">
                      {account.account_name} ({account.streaming.subscribed_instruments})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Toggle button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-arcade-dim hover:text-neon-blue focus:outline-none"
          >
            {expanded ? '▼' : '▶'} Details
          </button>
        </div>

        {/* Expanded view */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-dark-600">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* System Health Details */}
              <div>
                <h3 className="text-xs font-semibold text-arcade-text mb-2">System Health</h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">API:</span>
                    <span className={status.system.api === 'healthy' ? 'text-neon-green' : 'text-neon-pink'}>
                      {status.system.api}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">Database:</span>
                    <span className={status.system.database === 'healthy' ? 'text-neon-green' : 'text-neon-pink'}>
                      {status.system.database}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">Redis:</span>
                    <span className={status.system.redis === 'healthy' ? 'text-neon-green' : 'text-neon-pink'}>
                      {status.system.redis}
                    </span>
                  </div>
                </div>
              </div>

              {/* Worker Details */}
              <div>
                <h3 className="text-xs font-semibold text-arcade-text mb-2">Celery Workers</h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">Streaming Worker:</span>
                    <span className={status.workers.streaming_worker.status === 'active' ? 'text-neon-green' : 'text-arcade-dim'}>
                      {status.workers.streaming_worker.status} ({status.workers.streaming_worker.active_tasks} tasks)
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">Main Worker:</span>
                    <span className={status.workers.main_worker.status === 'active' ? 'text-neon-green' : 'text-arcade-dim'}>
                      {status.workers.main_worker.status} ({status.workers.main_worker.active_tasks} tasks)
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-arcade-muted">Beat Scheduler:</span>
                    <span className={status.workers.beat.status === 'active' ? 'text-neon-green' : 'text-arcade-dim'}>
                      {status.workers.beat.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Streaming Details */}
              <div>
                <h3 className="text-xs font-semibold text-arcade-text mb-2">Account Streaming</h3>
                {status.accounts.length === 0 ? (
                  <p className="text-xs text-arcade-dim">No accounts configured</p>
                ) : (
                  <div className="space-y-2">
                    {status.accounts.map((account) => (
                      <div key={account.account_id} className="border-l-2 border-dark-600 pl-2">
                        <div className="text-xs font-medium text-arcade-text">
                          {account.account_name} ({account.account_type})
                        </div>
                        <div className="text-xs text-arcade-muted">
                          Status:{' '}
                          <span className={account.streaming.active ? 'text-neon-green' : 'text-arcade-dim'}>
                            {account.streaming.active ? 'Streaming' : 'Inactive'}
                          </span>
                        </div>
                        <div className="text-xs text-arcade-muted">
                          Subscriptions: {account.streaming.subscribed_instruments}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Timestamp */}
            <div className="mt-3 pt-2 border-t border-dark-600 text-right">
              <span className="text-xs text-arcade-dim">
                Last updated: {new Date(status.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </footer>
  )
}
